/**
 * Pointeurs JSON (RFC 6901) sur un document d'éditeur, sans mutation : chaque opération rend un
 * nouveau document. Socle générique du Studio (gabarits de formulaire, pages web, pages GS1) : il ne
 * connaît aucun schéma, seulement des objets et des listes.
 */

export type Pointer = string;

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);

export function parsePointer(pointer: Pointer): string[] {
  if (pointer === '' || pointer === '/') return [];
  if (!pointer.startsWith('/')) throw new Error(`Pointeur JSON mal formé : « ${pointer} ».`);
  return pointer
    .slice(1)
    .split('/')
    .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
}

export function formatPointer(segments: readonly (string | number)[]): Pointer {
  return segments.length === 0 ? '' : `/${segments.map((segment) => String(segment).replace(/~/g, '~0').replace(/\//g, '~1')).join('/')}`;
}

export function childPointer(pointer: Pointer, segment: string | number): Pointer {
  return formatPointer([...parsePointer(pointer), segment]);
}

export function parentPointer(pointer: Pointer): { parent: Pointer; key: string } {
  const segments = parsePointer(pointer);
  if (segments.length === 0) throw new Error('La racine n’a pas de parent.');
  return { parent: formatPointer(segments.slice(0, -1)), key: segments[segments.length - 1]! };
}

export function getAt(document: unknown, pointer: Pointer): unknown {
  let node = document;
  for (const segment of parsePointer(pointer)) {
    if (Array.isArray(node)) node = node[Number(segment)];
    else if (isRecord(node)) node = node[segment];
    else return undefined;
  }
  return node;
}

/** Remplace (ou pose) la valeur au pointeur ; `undefined` retire la clé d'un objet. Les objets intermédiaires manquants sont créés. */
export function setAt<T>(document: T, pointer: Pointer, value: unknown): T {
  const segments = parsePointer(pointer);
  if (segments.length === 0) return value as T;
  const write = (node: unknown, index: number): unknown => {
    const segment = segments[index]!;
    const last = index === segments.length - 1;
    if (Array.isArray(node)) {
      const position = Number(segment);
      if (!Number.isInteger(position) || position < 0 || position > node.length) throw new Error(`Indice hors de la liste : ${pointer}.`);
      const copy = [...node];
      if (last) {
        if (value === undefined) copy.splice(position, 1);
        else copy[position] = value;
      } else copy[position] = write(node[position], index + 1);
      return copy;
    }
    const base = isRecord(node) ? node : {};
    const copy: Record<string, unknown> = { ...base };
    if (last) {
      if (value === undefined) delete copy[segment];
      else copy[segment] = value;
    } else copy[segment] = write(base[segment], index + 1);
    return copy;
  };
  return write(document, 0) as T;
}

export function removeAt<T>(document: T, pointer: Pointer): T {
  return setAt(document, pointer, undefined);
}

/** Insère un élément dans la liste désignée, à la position donnée (fin par défaut). */
export function insertAt<T>(document: T, listPointer: Pointer, item: unknown, position?: number): T {
  const list = getAt(document, listPointer);
  const current = Array.isArray(list) ? list : [];
  const at = position === undefined ? current.length : Math.max(0, Math.min(position, current.length));
  return setAt(document, listPointer, [...current.slice(0, at), item, ...current.slice(at)]);
}

/** Déplace un élément d'une liste à une autre position de la même liste. */
export function moveWithin<T>(document: T, listPointer: Pointer, from: number, to: number): T {
  const list = getAt(document, listPointer);
  if (!Array.isArray(list) || from < 0 || from >= list.length) return document;
  const target = Math.max(0, Math.min(to, list.length - 1));
  if (target === from) return document;
  const copy = [...list];
  const [item] = copy.splice(from, 1);
  copy.splice(target, 0, item);
  return setAt(document, listPointer, copy);
}

/**
 * Déplace un élément d'une liste vers une autre (ou la même), à la position donnée de la liste cible.
 * Le retrait se fait d'abord ; une cible dans la même liste après la source est décalée d'autant.
 */
export function moveAcross<T>(document: T, from: { list: Pointer; index: number }, to: { list: Pointer; index: number }): T {
  if (from.list === to.list) return moveWithin(document, from.list, from.index, to.index);
  const source = getAt(document, from.list);
  if (!Array.isArray(source) || from.index < 0 || from.index >= source.length) return document;
  const item = source[from.index];
  // La liste cible peut être contenue dans l'élément déplacé : refus, jamais une boucle.
  const fromItem = `${from.list}/${from.index}`;
  if (to.list === fromItem || to.list.startsWith(`${fromItem}/`)) return document;
  // Retirer d'abord change les indices de la cible si elle suit la source dans un parent commun.
  let target = to.list;
  const prefix = `${from.list}/`;
  if (target.startsWith(prefix)) {
    const [head, ...rest] = target.slice(prefix.length).split('/');
    const position = Number(head);
    if (Number.isInteger(position) && position > from.index) target = `${prefix}${position - 1}${rest.length > 0 ? `/${rest.join('/')}` : ''}`;
  }
  return insertAt(removeAt(document, fromItem), target, item, to.index);
}
